import {BaseAPISvc} from './BaseAPISvc.js';

/** @typedef {import('./Consultation.svc')} Types */

export class Consultation extends BaseAPISvc {
	constructor(...args) {
		'ngInject';
		super(...args);
		this.path = `${this.API}/v1/consultation`;
	}

	/** @param {Types.GetCustomersAndItsConsultationsParams} params */
	getCustomersAndItsConsultations(params, cache = false) {
		return this.http.get(`${this.path}/customersAndItsConsultations`, {params, cache});
	}

	/** @param {Types.GetEventLogsByConsultationIdParams} params */
	getEventLogsByConsultationId(params, cache = false) {
		return this.http.get(`${this.path}/eventLogsByConsultationId`, {params, cache});
	}

	/** @param {Types.PostCreateConsultationParams} params */
	postCreateConsultation(params) {
		return this.http.post(`${this.path}/createConsultation`, params);
	}

	/** @param {Types.PostUpdateConsultationNameParams} params */
	postUpdateConsultationName(params) {
		return this.http.post(`${this.path}/updateConsultationName`, params);
	}

	/** @param {Types.PostSaveConsultationStateParams} params */
	postSaveConsultationState(params) {
		return this.http.post(`${this.path}/saveConsultationState`, params);
	}

	/** @param {Types.PostSaveConsultationStateForCustomerParams} params */
	postSaveConsultationStateForCustomer(params) {
		const config = {headers: {Auth: this.PUBLIC_KEY}};
		return this.http.post(`${this.path}/saveConsultationStateForCustomer`, params, config);
	}

	/** @param {Types.PostUpdateAssignedConsultantParams} params */
	postUpdateAssignedConsultant(params) {
		return this.http.post(`${this.path}/updateAssignedConsultant`, params);
	}

	/** @param {Types.GetSurveyQuestionsParams} params */
	getSurveyQuestions(params, cache = false) {
		return this.http.get(`${this.path}/surveyQuestions`, {params, cache});
	}

	/** @param {Types.PostSaveConsultationCheckListAnswersParams} params */
	postSaveConsultationCheckListAnswers(params) {
		return this.http.post(`${this.path}/saveConsultationChecklistAnswers`, params);
	}

	/** @param {Types.GetNewSystemConfigurationParams} params */
	getNewSystemConfiguration({mock, ...params}, cache = false) {
		if (mock) return this.dbSvc.getNewSystemConfiguration(params);
		return this.http.get(`${this.path}/newSystemConfiguration`, {params, cache});
	}

	/** @param {Types.PostValidateConsultationStateAndStatusParams} params */
	postValidateConsultationStateAndStatus(params) {
		return this.http.post(`${this.path}/validateConsultationStateAndStatus`, params);
	}

	/** @param {Types.GetConsultationStateAndStatusForCustomerParams} params */
	getConsultationStateAndStatusForCustomer(params, cache = false) {
		const headers = {Auth: this.PUBLIC_KEY};
		return this.http.get(`${this.path}/consultationStateAndStatusForCustomer`, {params, cache, headers});
	}

	/** @param {Types.PostSaveNewSystemConfigurationParams} params */
	postSaveNewSystemConfiguration(params) {
		return this.http.post(`${this.path}/saveNewSystemConfiguration`, params);
	}

	/** @param {Types.PostAddSystemParams} params */
	postAddSystem({mock, ...params}) {
		if (mock) return this.dbSvc.postAddSystem(params);
		return this.http.post(`${this.path}/addSystem`, params);
	}

	/** @param {Types.PostRemoveSystemParams} params */
	postRemoveSystem(params) {
		return this.http.post(`${this.path}/removeSystem`, params);
	}

	/** @param {Types.PostCleanDataAssociatedToTheConsultationParams} params */
	postCleanDataAssociatedToTheConsultation(params) {
		return this.http.post(`${this.path}/cleanDataAssociatedToTheConsultation`, params);
	}

	/** @param {Types.PostAddAccessoryPlaceholderParams} params */
	postAddAccessoryPlaceholder(params) {
		return this.http.post(`${this.path}/addAccessoryPlaceholder`, params);
	}

	/** @param {Types.GetAllAccessoryPlaceholdersParams} params */
	getAllAccessoryPlaceholders(params, cache = false) {
		return this.http.get(`${this.path}/allAccessoryPlaceholders`, {params, cache});
	}

	/** @param {Types.PostUpdateEquipmentPricingProfileOnConsultationParams} params */
	postUpdateEquipmentPricingProfileOnConsultation(params) {
		return this.http.post(`${this.path}/updateEquipmentPricingProfileOnConsultation`, params);
	}

	/** @param {Object} params */
	postRemoveAccessoryPlaceholder(params) {
		return this.http.post(`${this.path}/removeAccessoryPlaceholder`, params);
	}

	/** @param {Types.PostUpdateSystemsPriceByEquipmentPricingProfileParams} params */
	postUpdateSystemsPriceByEquipmentPricingProfile(params) {
		return this.http.post(`${this.path}/updateSystemsPriceByEquipmentPricingProfile`, params);
	}

	/** @param {Types.PostUpdateSystemNameParams} params */
	postUpdateSystemName(params) {
		return this.http.post(`${this.path}/updateSystemName`, params);
	}

	/** @param {Types.GetConsultationHasAccessoriesParams} params */
	getConsultationHasAccessories(params, cache = false) {
		return this.http.get(`${this.path}/consultationHasAccessories`, {params, cache});
	}

	/** @param {Types.PostConvertUnmatchedFinanceOptionsToDefaultParams} params */
	postConvertUnmatchedFinanceOptionsToDefault(params) {
		return this.http.post(`${this.path}/convertUnmatchedFinanceOptionsToDefault`, params);
	}

	/** @param {Types.PostToggleShowSavingsValueParams} params */
	postToggleShowSavingsValue(params) {
		return this.http.post(`${this.path}/togglesHowSavingsValue`, params);
	}

	/** @param {Types.PostUpdateShowEnergySavingsSettingsParams} params */
	postUpdateShowEnergySavingsSettings(params) {
		return this.http.post(`${this.path}/updateShowEnergySavingsSettings`, params);
	}

	/** @param {Types.PostUpdateTotalInvestmentAndMonthlyPaymentSettingParams} params */
	postUpdateTotalInvestmentAndMonthlyPaymentSetting(params) {
		return this.http.post(`${this.path}/updatetotalinvestmentandmonthlypaymentsetting`, params);
	}

	/** @param {Types.PostSaveEstimatedStartAndEndDateParams} params */
	postSaveEstimatedStartAndEndDate(params) {
		return this.http.post(`${this.path}/saveEstimatedStartAndEndDate`, params);
	}

	/** @param {Types.GetEstimatedStartAndEndDateParams} params */
	getEstimatedStartAndEndDate(params, cache = false) {
		return this.http.get(`${this.path}/estimatedStartAndEndDate`, {params, cache});
	}

	/** @param {Types.GetEstimatedStartAndEndDateParams} params */
	getEstimatedStartAndEndDateForCustomer(params, cache = false) {
		const headers = {Auth: this.PUBLIC_KEY};
		return this.http.get(`${this.path}/estimatedStartAndEndDateForCustomer`, {params, cache, headers});
	}

	/** @param {Types.PostPresentConsultationParams} params */
	postPresentConsultation(params) {
		return this.http.post(`${this.path}/presentConsultation`, params);
	}

	/** @param {Types.PostRevertToDraftParams} params */
	postRevertToDraft(params) {
		return this.http.post(`${this.path}/revertToDraft`, params);
	}

	/** @param {Types.PostUpdateSystemNotesParams} params */
	postUpdateSystemNotes(params) {
		return this.http.post(`${this.path}/updateSystemNotes`, params);
	}

	/** @param {Types.PostAcceptProposalParams} params */
	postAcceptProposal(params) {
		return this.http.post(`${this.path}/acceptProposal`, params);
	}

	/** @param {Types.PostAcceptProposalParams} params */
	postCustomerAcceptProposal(params) {
		const config = {headers: {Auth: this.PUBLIC_KEY}};
		return this.http.post(`${this.path}/customerAcceptProposal`, params, config);
	}

	/** @param {Types.GetConsultationFromLegacyPresentedProposalParams} params */
	getConsultationFromLegacyPresentedProposal(params, cache = false) {
		const headers = {Auth: this.PUBLIC_KEY};
		return this.http.get(`${this.path}/consultationFromLegacyPresentedProposal`, {params, cache, headers});
	}

	/** @param {Types.GetConsultationFromLegacyPresentedProposalWithPositionParams} params */
	getConsultationFromLegacyPresentedProposalWithPosition(params, cache = false) {
		const headers = {Auth: this.PUBLIC_KEY};
		return this.http.get(`${this.path}/consultationFromLegacyPresentedProposalWithPosition`, {
			params,
			cache,
			headers,
		});
	}

	/** @param {Types.GetConsultationFromLegacyAcceptedProposalParams} params */
	getConsultationFromLegacyAcceptedProposal(params) {
		const headers = {Auth: this.PUBLIC_KEY};
		return this.http.get(`${this.path}/consultationFromLegacyAcceptedProposal`, {params, headers});
	}

	/** @param {Types.PostLogExpiredConsultationEventParams} params */
	postLogExpiredConsultationEvent(params) {
		const config = {headers: {Auth: this.PUBLIC_KEY}};
		return this.http.post(`${this.path}/logExpiredConsultationEvent`, params, config);
	}

	/** @param {Types.PostLogBlockedConsultationEventParams} params */
	postLogBlockedConsultationEvent(params) {
		const config = {headers: {Auth: this.PUBLIC_KEY}};
		return this.http.post(`${this.path}/logBlockedConsultationEvent`, params, config);
	}

	/** @param {Types.PostUnAcceptConsultationFromAppParams} params */
	postUnAcceptConsultationFromApp(params) {
		return this.http.post(`${this.path}/unacceptConsultationFromApp`, params);
	}

	/** @param {Types.PostAddSurveyItemAttachmentParams} params */
	postAddSurveyItemAttachment(params) {
		return this.http.post(`${this.path}/addSurveyItemAttachment`, params);
	}

	/** @param {Types.PostRemoveMediaFromConsultationChecklistParams} params */
	postRemoveMediaFromConsultationChecklist(params) {
		return this.http.post(`${this.path}/removeMediaFromConsultationChecklist`, params);
	}

	/** @param {Types.PostUpdateDefaultEquipmentParams} params */
	postUpdateDefaultEquipment(params) {
		return this.http.post(`${this.path}/updateDefaultEquipment`, params);
	}

	/** @param {Types.GetRevenuesReportInAPeriodOfTimeParams} params */
	getRevenuesReportInAPeriodOfTime(params, cache = false) {
		return this.http.get(`${this.path}/revenuesReportInAPeriodOfTime`, {params, cache});
	}

	/** @param {Types.PostAddJobParams} params */
	postAddJob(params) {
		return this.http.post(`${this.path}/addJob`, params);
	}

	/** @param {Types.PostRecommendJobParams} params */
	postRecommendJob(params) {
		return this.http.post(`${this.path}/recommendJob`, params);
	}

	/** @param {Types.PostArchiveConsultationParams} params */
	postArchiveConsultation(params) {
		return this.http.post(`${this.path}/archiveConsultation`, params);
	}

	/** @param {Types.PostArchiveConsultationParams} params */
	postUnarchiveConsultation(params) {
		return this.http.post(`${this.path}/unarchiveConsultation`, params);
	}

	/** @param {Types.GetAllConsultationStatusesParams} params */
	getAllConsultationStatuses(params, cache = false) {
		return this.http.get(`${this.path}/allConsultationStatuses`, {params, cache});
	}

	/** @param {Types.PostLogViewedLostEventParams} params */
	postLogViewedLostEvent(params) {
		const config = {headers: {Auth: this.PUBLIC_KEY}};
		return this.http.post(`${this.path}/logViewedLostEvent`, params, config);
	}

	/** @param {Types.GetAllViewByDateOptionsParams} params */
	getAllViewByDateOptions(params, cache = false) {
		return this.http.get(`${this.path}/viewByDateFilterOptionsByConsultationStatusesFilter`, {params, cache});
	}

	/** @param {Types.PostMarkAsLostParams} params */
	postMarkAsLost(params) {
		return this.http.post(`${this.path}/markAsLost`, params);
	}

	/** @param {Types.PostRevertLostStatusParams} params */
	postRevertLostStatus(params) {
		return this.http.post(`${this.path}/revertLostStatus`, params);
	}

	/** @param {Types.GetLostStatusReasonsParams} params */
	getLostStatusReasons(params, cache = false) {
		return this.http.get(`${this.path}/lostStatusReasons`, {params, cache});
	}

	/** @param {Types.GetDetailsParams} params */
	getDetails(params, cache = false) {
		return this.http.get(`${this.path}/details`, {params, cache});
	}

	/** @param {Types.GetSideNotesParams} params */
	getSideNotes(params, cache = false) {
		return this.http.get(`${this.path}/notes`, {params, cache});
	}

	/** @param {Types.GetMetadataParams} params */
	getMetadata(params, cache = false) {
		return this.http.get(`${this.path}/metadata`, {params, cache});
	}

	/** @param {Types.PostAddSideNoteParams} params */
	postAddSideNote(params) {
		return this.http.post(`${this.path}/addNote`, params);
	}

	/** @param {Types.PostEditSideNoteParams} params */
	postEditSideNote(params) {
		return this.http.post(`${this.path}/editNote`, params);
	}

	/** @param {Types.PostRemoveSideNoteParams} params */
	postRemoveSideNote(params) {
		return this.http.post(`${this.path}/removeNote`, params);
	}

	/** @param {Types.PostToggleShowNoteOnInspectorParams} params */
	postToggleShowNoteOnInspector(params) {
		return this.http.post(`${this.path}/togglesHowNoteOnInspector`, params);
	}

	/** @param {Types.GetConsultationExpirationDateParams} params */
	getConsultationExpirationDate(params, cache = false) {
		return this.http.get(`${this.path}/consultationExpirationDate`, {params, cache});
	}

	/** @param {Types.PostUpdateConsultationExpirationDateParams} params */
	postUpdateConsultationExpirationDate(params) {
		return this.http.post(`${this.path}/updateConsultationExpirationDate`, params);
	}

	/** @param {Types.PostResetConsultationExpirationDateParams} params */
	postResetConsultationExpirationDate(params) {
		return this.http.post(`${this.path}/resetConsultationExpirationDate`, params);
	}

	/** @param {Types.GetConsultationMatchesParams} params */
	getConsultationMatches(params, cache = false) {
		return this.http.get(`${this.path}/consultationMatches`, {params, cache});
	}

	/** @param {Types.GetFuzzySearchProductsByTermParams} params */
	getFuzzySearchProductsByTerm(params, cache = false) {
		return this.http.get(`${this.path}/fuzzySearchProductsByTerm`, {params, cache});
	}

	/** @param {Types.PostSendMatchesToServiceTitanParams} params */
	postSendMatchesToServiceTitan(params) {
		return this.http.post(`${this.path}/sendMatchesToServiceTitan`, params);
	}

	/** @param {Types.GetConsultationPostingSnapshotParams} params */
	getConsultationPostingSnapshot(params, cache = false) {
		return this.http.get(`${this.path}/consultationPostingSnapshot`, {params, cache});
	}

	/** @param {Types.PostReplaceEquipmentInSystemParams} params */
	postReplaceEquipmentInSystem(params) {
		return this.http.post(`${this.path}/replaceEquipmentInSystem`, params);
	}

	/**
	 * @param {Object} params
	 * @param {number} params.serviceProviderId
	 * @param {number} params.userId
	 * @param {number} params.consultationId
	 * @param {boolean} [cache=false]
	 * @returns {Promise}
	 */
	getAiSummaryNotesPreview(params, cache = false) {
		return this.http.get(`${this.path}/aiSummaryNotesPreview`, {params, cache});
	}

	/**
	 * @param {Object} params
	 * @param {number} params.serviceProviderId
	 * @param {number} params.userId
	 * @param {number} params.consultationId
	 * @param {boolean} [cache=false]
	 * @returns {Promise}
	 */
	getAiSummaryDetails(params, cache = false) {
		return this.http.get(`${this.path}/aiSummaryDetails`, {params, cache});
	}

	/**
	 * @param {Object} params
	 * @param {number} params.serviceProviderId
	 * @param {number} params.userId
	 * @param {number} params.consultationId
	 * @returns {Promise}
	 */
	postGenerateSummaries(params) {
		return this.http.post(`${this.path}/generateSummaries`, params);
	}

	/**
	 * @param {Object} params
	 * @param {number} params.serviceProviderId
	 * @param {number} params.userId
	 * @param {number} params.consultationId
	 * @param {Array} params.proposalIds
	 * @returns {Promise}
	 */
	postGenerateSummariesByProposals(params) {
		return this.http.post(`${this.path}/generateSummariesByProposals`, params);
	}

	/**
	 * @param {Object} params
	 * @param {number} params.serviceProviderId
	 * @param {number} params.userId
	 * @param {number} params.consultationId
	 * @param {Array} params.summaries
	 * @returns {Promise}
	 */
	postUpsertAiSummaries(params) {
		return this.http.post(`${this.path}/upsertAiSummaries`, params);
	}
}
//
let compMod = angular.module('mod.svc.consultationService', []).service('ConsultationService', Consultation);
export default (compMod = compMod.name);
